// Data + triage helpers for the Skill Resolution page.
//
// The Queue tab is wired to the real API
// (`GET /internal/admin/skill-resolution/queue` — see
// src/services/api/queries/skillResolutionQueries.js). The reference-skill
// picker (Alias/Map/Unmap target) is wired too, via
// `useSkillsList()` (src/services/api/queries/referenceDataQueries.js) —
// see the `refSkills` state in SkillResolution.jsx. Everything else below is
// still mock data standing in for pieces that don't have an endpoint yet:
// the Borderline audit and Health tabs, and the resolve/ignore/block/unmap
// actions themselves. Shapes are modelled on their documented endpoints so
// swapping in real queries later is a find-and-replace of the data source,
// not a redesign.

// Unused fallback — kept only in case a reference-skill list is ever needed
// before `useSkillsList()`'s query resolves. Not the picker's data source
// anymore (see the comment above).
export const REF_SKILLS = [
  { id: 9, name: 'Risk Management' },
  { id: 14, name: 'Operational Risk' },
  { id: 41, name: 'Basel Framework' },
  { id: 118, name: 'Accounts Payable' },
  { id: 180, name: 'Python' },
  { id: 222, name: 'SQL' },
  { id: 301, name: 'Credit Analysis' },
  { id: 355, name: 'Portfolio Management' },
  { id: 414, name: 'Equity Research' },
  { id: 425, name: 'Financial Analysis' },
  { id: 512, name: 'Tableau' },
  { id: 544, name: 'Power BI' },
  { id: 607, name: 'Customer Service' },
  { id: 640, name: 'Microsoft Office' },
  { id: 641, name: 'Microsoft Excel' },
  { id: 702, name: 'Anti-Money Laundering' },
];

export const refById = (id) => REF_SKILLS.find((r) => r.id === id);

// Same lookup, but against a (possibly grown) ref-skills list held in state.
export const findRefSkill = (skills, id) => skills.find((r) => r.id === id);

// Adapts one raw `GET /skill-resolution/queue` row into the shape the
// drawer/action dialog work with, and layers on any local, not-yet-persisted
// action (see `overrides` in SkillResolution.jsx — there's no
// resolve/ignore/block endpoint yet, so an action taken in the UI is applied
// optimistically here rather than by refetching).
export function adaptQueueItem(raw, override) {
  const nearest = raw.top_nearest
    ? [
        [
          raw.top_nearest.ref_skill_id,
          raw.top_nearest.name,
          raw.top_nearest.count ?? 0,
          raw.top_nearest.score ?? 0,
        ],
      ]
    : [];
  return {
    norm: raw.name_norm,
    status: override?.status ?? raw.status,
    cand: raw.occurrences?.candidate ?? 0,
    req: raw.occurrences?.requirement ?? 0,
    mandatory: !!raw.touches_mandatory_requirement,
    // No per-variant counts from this endpoint yet — render name-only chips.
    variants: (raw.raw_variants ?? []).map((v) => [v, null]),
    nearest,
    exact: raw.exact_hit
      ? {
          id: raw.exact_hit.ref_skill_id,
          name: raw.exact_hit.name,
          via: 'canonical name_norm',
        }
      : null,
    lastSeen: raw.last_seen,
    // Not returned by the list endpoint — the "would heal" preview in the
    // Decision panel assumes nothing is skipped until a detail endpoint says
    // otherwise.
    skipped: 0,
    reqs: [],
    reqsDetailAvailable: false,
    sampleIds: [],
    hasHistory: !!raw.has_history,
    history: override?.history ?? [],
    apiSuggestedAction: raw.suggested_action,
    healedTo: override?.healedTo ?? null,
  };
}

// Layers the record-detail endpoint's real numbers on top of an already
// list-adapted item (see adaptQueueItem above) — real variant counts, a
// real nearest-match distribution, the affected candidate/requirement rows,
// a real skipped/would-heal preview, and real history. Falls back to
// whatever the list-only item already had for any field the detail
// response doesn't cover, so the drawer renders sensibly before the detail
// finishes loading (or if it fails).
export function mergeQueueDetail(item, detail) {
  if (!item || !detail || detail.name_norm !== item.norm) return item;

  const variants =
    Array.isArray(detail.raw_variants) && detail.raw_variants.length
      ? detail.raw_variants.map((v) => [v.name, v.count])
      : item.variants;

  // Defensive: the API's exact field names/shapes for these nested arrays
  // are still settling (e.g. `affected.requirements` may not always be a
  // bare array), so anything that isn't actually an array falls back to
  // empty rather than crashing on `.map`.
  const nearest = Array.isArray(detail.nearest_distribution)
    ? detail.nearest_distribution.map((n) => [
        n.ref_skill_id ?? n.id,
        n.name,
        n.count ?? n.rows ?? 0,
        n.score ?? n.avg_score ?? 0,
      ])
    : [];

  // `affected.requirements` is a summary object, not a bare array —
  // `{count, any_mandatory, sample}`, with `sample` being a single example
  // row (real shape, confirmed live). Only surface that example when it's
  // mandatory: a non-mandatory touch isn't worth spending review attention
  // on, and the count above already says how many rows are touched either
  // way. `reqsDetailAvailable` distinguishes "detail came back with no
  // mandatory example" from "detail hasn't loaded/returned anything for
  // this field yet" so the empty-state copy can say the right thing.
  const reqSummary = detail.affected?.requirements;
  const reqSample = !Array.isArray(reqSummary) ? reqSummary?.sample : null;
  const reqs = reqSample?.is_mandatory
    ? [
        {
          id: reqSample.requirement_id,
          title: reqSample.name || `Requirement ${reqSample.requirement_id}`,
          mandatory: true,
        },
      ]
    : [];
  const reqsDetailAvailable = !!reqSummary;

  const sampleIds = Array.isArray(detail.affected?.candidates?.sample_ids)
    ? detail.affected.candidates.sample_ids
    : item.sampleIds;

  // Candidate + requirement rows the suggested/exact target would skip —
  // real per-target number instead of the list-only guess of zero. Combined
  // with the real cand/req totals from the list, this makes the existing
  // "rows would heal" = healable - skipped math exact.
  const skipped = detail.collision_preview
    ? (detail.collision_preview.skipped?.candidate ?? 0) +
      (detail.collision_preview.skipped?.requirement ?? 0)
    : item.skipped;

  const detailHistory = Array.isArray(detail.history)
    ? detail.history.map((h) => ({
        action: h.action,
        actor: h.actor_email || 'unknown',
        at: h.created_at,
        payload: h.payload,
        result: h.result,
      }))
    : [];

  return {
    ...item,
    variants: variants.length ? variants : item.variants,
    nearest: nearest.length ? nearest : item.nearest,
    exact: item.exact
      ? { ...item.exact, via: detail.exact_hit?.via ?? item.exact.via }
      : item.exact,
    reqs,
    reqsDetailAvailable,
    sampleIds,
    skipped,
    // Local, not-yet-persisted actions (`item.history`, from `overrides`)
    // are newer than anything the server knows about — keep them last so
    // the reversed, newest-first render puts them at the top.
    history: [...detailHistory, ...item.history],
    detailLoaded: true,
  };
}

export const BORDERLINE = [
  {
    side: 'candidate',
    row: '6b31ef1b',
    name: 'Sales Support',
    norm: 'sales support',
    score: 0.781,
    to: [607, 'Customer Service'],
  },
  {
    side: 'candidate',
    row: 'a10c7e92',
    name: 'Vendor Coordination',
    norm: 'vendor coordination',
    score: 0.784,
    to: [607, 'Customer Service'],
  },
  {
    side: 'requirement',
    row: 'REQ-173-4',
    name: 'Regulatory Reporting',
    norm: 'regulatory reporting',
    score: 0.803,
    to: [41, 'Basel Framework'],
  },
  {
    side: 'candidate',
    row: '4c8b1a67',
    name: 'Data Entry Operations',
    norm: 'data entry operations',
    score: 0.806,
    to: [222, 'SQL'],
  },
  {
    side: 'requirement',
    row: 'REQ-190-2',
    name: 'Statistical Modelling',
    norm: 'statistical modelling',
    score: 0.812,
    to: [425, 'Financial Analysis'],
  },
  {
    side: 'candidate',
    row: '77e2b0d4',
    name: 'Client Servicing',
    norm: 'client servicing',
    score: 0.815,
    to: [607, 'Customer Service'],
  },
  {
    side: 'candidate',
    row: 'c1d9e802',
    name: 'Spreadsheet Modelling',
    norm: 'spreadsheet modelling',
    score: 0.819,
    to: [641, 'Microsoft Excel'],
  },
];

export const HEALTH = {
  mix: {
    candidate: [
      ['EXACT', 0.612],
      ['EMBEDDING', 0.244],
      ['BACKFILL', 0.08],
      ['NONE', 0.052],
      ['(legacy)', 0.012],
    ],
    requirement: [
      ['EXACT', 0.704],
      ['EMBEDDING', 0.221],
      ['BACKFILL', 0.044],
      ['NONE', 0.028],
      ['(legacy)', 0.003],
    ],
  },
  dead: {
    candidate: { unresolved: 1847, total: 28912 },
    requirement: { unresolved: 96, total: 3104 },
  },
  backfilled: { candidate: 3298, requirement: 114 },
};

export const HEALTH_MIX_COLORS = {
  EXACT: 'var(--ds-success)',
  EMBEDDING: 'hsl(var(--primary))',
  BACKFILL: '#8B5CF6',
  NONE: 'var(--ds-warning)',
  '(legacy)': 'var(--muted-foreground)',
};

export const STATUS_META = {
  open: { label: 'Open', dotClass: 'bg-primary' },
  ignored: { label: 'Ignored', dotClass: 'bg-ds-warning' },
  blocked: { label: 'Blocked', dotClass: 'bg-ds-danger' },
  resolved: { label: 'Resolved', dotClass: 'bg-ds-success' },
};

// suggested_action is a triage hint only — never something the server acts on
// automatically. Mirrors the same rule the prototype/backend spec uses.
export function suggestAction(item) {
  // The API's own `suggested_action` (once it's populated server-side) is
  // authoritative — only fall back to this client-side heuristic when it's
  // absent, e.g. for mock/borderline data or while the backend hasn't
  // computed a suggestion for a given norm yet.
  if (item.apiSuggestedAction) return item.apiSuggestedAction;
  if (item.exact) return 'MAP';
  const total = item.cand + item.req;
  if (total < 3) return 'IGNORE';
  if (item.nearest.length && item.nearest[0][3] >= 0.7) return 'ALIAS';
  if (item.nearest.length) return 'NEW_SKILL';
  return null;
}

export const SUGGEST_META = {
  ALIAS: { label: 'Alias', className: 'bg-brand-bg-subtle text-primary' },
  NEW_SKILL: {
    label: 'New skill',
    className:
      'bg-[#F3F0FF] text-[#7C3AED] dark:bg-[#251D40] dark:text-[#C4B5FD]',
  },
  MAP: { label: 'Map only', className: 'bg-ds-success-subtle text-ds-success' },
  IGNORE: { label: 'Ignore', className: 'bg-muted text-muted-foreground' },
  BLOCK: { label: 'Block', className: 'bg-ds-danger-subtle text-ds-danger' },
};

export function healableCount(item) {
  return item.cand + item.req;
}

export function skippedCount(item) {
  return item.skipped || 0;
}
